import type { ModalName } from "@/components/modal/modals";
import type { BoardType } from "@/model/baseTypes";
import { isFlexBoard, isTeamBoard } from "@/model/board";
import type { LinkingTarget } from "@/model/link";
import type { Art, Session, Team } from "@/model/session";
import { goToBoard } from "@/router/navigation";
import { useBoardStore } from "@/store/board";
import { useModalStore } from "@/store/modal";
import { useNavigationStore } from "@/store/navigation";
import { useSessionStore } from "@/store/session";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";
import type { UnknownRecord } from "@/utils/type";

import { action, ctxAction, defineActions, untrackedAction } from "./actions";
import type { ActionContext } from "./types";

export const navigationActions = defineActions("navigation", {
  changeBoard: ctxAction((ctx, board) => trackedGoToBoard(ctx, board)),
  goToSolutionBacklog: ctxAction((ctx) => trackedGoToBoard(ctx, "solution_backlog"), {
    name: /*$t*/ "action.solutionBacklogBoard",
    icon: "board/solution-backlog",
    active: () => useBoardStore().board?.type === "solution_backlog",
  }),
  goToSolution: ctxAction((ctx) => trackedGoToBoard(ctx, "solution"), {
    name: /*$t*/ "action.solutionBoard",
    icon: "board/solution-planning",
    active: () => useBoardStore().board?.type === "solution",
  }),
  goToBacklog: ctxAction((ctx) => trackedGoToBoard(ctx, "backlog"), {
    name: /*$t*/ "action.backlogBoard",
    icon: "board/backlog",
    active: () => useBoardStore().board?.type === "backlog",
  }),
  goToProgram: ctxAction((ctx) => trackedGoToBoard(ctx, "program"), {
    name: /*$t*/ "action.programBoard",
    icon: "board/planning",
    active: () => useBoardStore().board?.type === "program",
  }),
  goToObjectives: ctxAction((ctx) => trackedGoToBoard(ctx, "objectives"), {
    name: /*$t*/ "action.objectivesBoard",
    icon: "board/objectives-art",
    active: () => useBoardStore().board?.type === "objectives",
  }),
  goToRisk: ctxAction((ctx) => trackedGoToBoard(ctx, "risk"), {
    name: /*$t*/ "action.riskBoard",
    icon: "board/risk",
    active: () => useBoardStore().board?.type === "risk",
  }),
  goToTeam: ctxAction((ctx) => trackedGoToBoard(ctx, "team"), {
    name: /*$t*/ "action.teamBoard",
    icon: "board/team",
    active: () => useBoardStore().board?.type === "team",
  }),

  goToCanvas: untrackedAction((ctx) => openModal(ctx, "FlexModal"), {
    name: /*$t*/ "action.canvases",
    icon: "board/canvases",
    active: () => useBoardStore().board?.type === "flex",
  }),
  changeCanvas: action((name: string) => goToBoard({ type: "flex", name })),

  goToPlanReadout: untrackedAction((ctx) => openModal(ctx, "PlanReadoutModal"), {
    name: /*$t*/ "planReadout.title",
    icon: "general/plan-readout",
  }),

  selectSession: action((session: Session, boardType: BoardType) =>
    goToBoard({ type: boardType, session: useSessionStore().uniqueSessionName(session) }),
  ),
  changeSession: ctxAction((ctx) => {
    ctx.track({});
    openModal(ctx, "SessionSelectionModal");
  }),
  changeArt: ctxAction((ctx, art: Art) => {
    const userTeams = useUserStore().technicalUser.teams;
    const matchesUsersArt = userTeams.some((t) => t.artId === art.id);
    ctx.track({ artId: art.id, matchesUsersArt });

    useNavigationStore().setArtId(art.id);
    void goToBoard({ type: useBoardStore().currentBoard().type, artId: art.id });
  }),
  changeTeam: ctxAction((ctx, team: Team) => {
    const userTeams = useUserStore().technicalUser.teams;
    const matchesUsersTeam = userTeams.some((t) => t.id === team.id);
    ctx.track({ teamId: team.id, matchesUsersTeam });

    useNavigationStore().setTeamInArt(team);
    if (team.artId) {
      useNavigationStore().setArtId(team.artId);
    }
    void goToBoard({ type: useBoardStore().currentBoard().type, team });
  }),

  showMyWork: ctxAction(
    (ctx, attrs?: { iterationId: number }) => {
      const iterationId = attrs?.iterationId ?? useSessionStore().currentIteration?.id;
      if (
        useWorkModeStore().isExecutionMode &&
        isTeamBoard(useBoardStore().board?.type) &&
        iterationId !== undefined
      ) {
        ctx.track({});
        const userIds = [useUserStore().technicalUser.id];
        navigationActions.showKanban(ctx, { iterationId, userIds });
      } else {
        ctx.track(null);
      }
    },
    { name: /*$t*/ "general.myWork" },
  ),
  showKanban: untrackedAction(
    (ctx, attrs?: { iterationId?: number; userIds?: string[] }) => {
      const boardType = useBoardStore().currentBoard().type;
      switch (boardType) {
        case "backlog":
        case "program":
          return openModal(ctx, "ArtKanbanModal", { attrs: { boardType } });
        case "solution_backlog":
        case "solution":
          return openModal(ctx, "SolutionKanbanModal", { attrs: { boardType } });
        case "team": {
          if (!useWorkModeStore().isExecutionMode) {
            return ctx.track(null);
          }
          const iterationId = attrs?.iterationId ?? useSessionStore().nearestIteration?.id;
          return iterationId === undefined
            ? ctx.track(null)
            : openModal(ctx, "IterationKanbanModal", {
                attrs: { iterationId, userIds: attrs?.userIds },
              });
        }
        default:
          ctx.track(null);
      }
    },
    { name: /*$t*/ "general.kanbanBoard", icon: "board/kanban" },
  ),
  showBreakdown: ctxAction(
    (ctx, target?: LinkingTarget) => {
      if (target) {
        openModal(ctx, "BreakdownModal", { attrs: { target } });
      } else {
        // When triggered from the action menu, we have to infer the target
        const cards = useBoardStore().selectedOrActiveStickies;

        if (cards.length === 1) {
          const id = cards[0]?.data.id;
          openModal(ctx, "BreakdownModal", { attrs: { target: { type: "sticky", id } } });
        }
      }
    },
    { name: /*$t*/ "action.breakdown", icon: "board/breakdown" },
  ),

  openModal: ctxAction((ctx, modalName: ModalName, options?: { attrs?: UnknownRecord }) => {
    ctx.track({ modalName });
    useModalStore().open(modalName, options);
  }),
  closeModal: ctxAction((ctx) => {
    ctx.track({ modalName: useModalStore().modal?.name ?? "unknown" });
    useModalStore().close();
  }),
});

function trackedGoToBoard(ctx: ActionContext, boardType: BoardType) {
  ctx.track({ fromBoard: useBoardStore().currentBoard().type, toBoard: boardType });
  useNavigationStore().setBoardType(boardType);
  if (isFlexBoard(boardType)) {
    openModal(ctx, "FlexModal");
  } else {
    void goToBoard(boardType);
  }
}

function openModal(ctx: ActionContext, name: ModalName, options?: { attrs?: UnknownRecord }) {
  navigationActions.openModal(ctx, name, options);
}
